import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, Skeleton, IconButton } from "@mui/material";

import { EditIcon } from "../../../../../assets/icons/go-service-icons";
import EmptyServicesFigure from "../../../../../assets/images/empty-services.png";

import AddServiceModal from "./AddServiceModal";

const ServiceOverview = ({
  serviceOverview,
  isLoading,
  isOwn,
  userId,
}: {
  serviceOverview?: string;
  isLoading: boolean;
  isOwn?: boolean;
  userId?: string;
}) => {
  const { t } = useTranslation("profiles", { keyPrefix: "goService" });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);
  const [isAddOverviewModalOpen, setIsAddOverviewModalOpen] = useState(false);
  const handleAddOverview = () => setIsAddOverviewModalOpen(true);
  const handleCloseAddOverviewModal = () => setIsAddOverviewModalOpen(false);

  useEffect(() => {
    const textElement = textRef.current;
    if (textElement) {
      const { clientHeight, scrollHeight } = textElement;
      if (clientHeight < scrollHeight) {
        setIsClamped(true);
      }
    }
  }, [serviceOverview]);

  const toggleExpansion = () => {
    setIsExpanded((prev) => !prev);
  };

  if (isLoading) <ServiceOverviewLoader />;

  return (
    <Box>
      <Typography sx={styles.serviceOverviewTitle}>
        {t("serviceOverview")}
        {isOwn && (
          <IconButton onClick={handleAddOverview} size="small">
            <EditIcon />
          </IconButton>
        )}
        <AddServiceModal
          open={isAddOverviewModalOpen}
          onClose={handleCloseAddOverviewModal}
          userId={userId}
          overview={serviceOverview}
        />
      </Typography>
      {!serviceOverview && (
        <>
          {isOwn && <NoServiceOverviewOwnProfile userId={userId} />}
          {!isOwn && <Typography sx={styles.overViewTextStyle}>{t("noServiceOverview")}</Typography>}
        </>
      )}
      {serviceOverview && (
        <>
          <Typography
            ref={textRef}
            sx={{
              ...styles.overViewTextStyle,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              WebkitLineClamp: isExpanded ? "none" : 4,
            }}
          >
            {serviceOverview}
          </Typography>
          {isClamped && (
            <Button
              onClick={toggleExpansion}
              variant="text-sm"
              sx={{
                marginTop: "10px",
                padding: 0,
                "&:hover": {
                  background: "none",
                },
              }}
            >
              {isExpanded ? t("readLess") : t("readMore")}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default ServiceOverview;

const ServiceOverviewLoader = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "goService" });

  return (
    <Box sx={styles.serviceOverviewContainer}>
      <Typography sx={styles.serviceOverviewTitle}>{t("serviceOverview")}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Skeleton variant="text" height={20} width="100%" />
        <Skeleton variant="text" height={20} width="100%" />
        <Skeleton variant="text" height={20} width="100%" />
        <Skeleton variant="text" height={20} width="35%" />
      </Box>
    </Box>
  );
};

const NoServiceOverviewOwnProfile = ({ userId }: { userId?: string }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "goService" });
  const [isAddOverviewModalOpen, setIsAddOverviewModalOpen] = useState(false);
  const handleAddOverview = () => setIsAddOverviewModalOpen(true);
  const handleCloseAddOverviewModal = () => setIsAddOverviewModalOpen(false);

  return (
    <Box sx={styles.emptyContainer}>
      <Box sx={styles.emptyImage} component="img" src={EmptyServicesFigure} alt="Empty Services" />
      <Typography sx={styles.emptyText}>{t("tellOthersMoreAboutYourService")}</Typography>
      <Button variant="outlined-sm" onClick={handleAddOverview}>
        {t("addOverview")}
      </Button>
      <AddServiceModal open={isAddOverviewModalOpen} onClose={handleCloseAddOverviewModal} userId={userId} />
    </Box>
  );
};

const styles = {
  serviceOverviewContainer: {
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    padding: "20px",
  },
  serviceOverviewTitle: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  serviceOverviewText: {
    color: "var(--Gray-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    fontFamily: "Montserrat",
    marginBottom: "10px",
  },
  overViewTextStyle: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  emptyContainer: {
    padding: "0 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    alignItems: "center",
  },
  emptyTitle: {
    width: "100%",
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
  },
  emptyImage: {
    width: "186px",
    height: "138.966px",
  },
  emptyText: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  createButton: {
    width: "fit-content",
  },
};
